import { environmentHttpClient } from "client/Api/httpClient";
import { PermissionsMode } from "client/Api/SharePointSettings/sharepointSettings";
import { SharePointMember, SharePointPeoplePickerItemModel } from "./sharePointSharing";

export type LanguageListItem = {
	id: number;
	name: string;
};

export type SiteTemplateListItem = {
	name: string;
	title: string;
};

export type NewSubsiteViewModel = {
	availableLanguages: LanguageListItem[];
	availableTemplates: SiteTemplateListItem[];
	defaultPermissionsMode: PermissionsMode;
	siteUrl: string;
	defaultDescription: string;
	defaultLanguage: number;
	canUserChangeDescription: boolean;
	canUserChangeLanguage: boolean;
	canUserChangePermissionsMode: boolean;
};

export type CreateSubsiteModel = {
	siteUrl: string;
	name: string;
	description: string;
	url: string;
	language: number;
	template: string;
	permissionsMode: PermissionsMode;
};

const controllerRelativeUrl = "Documents/SharePoint/Sites";

export function getNewSubsiteViewModel(
	siteUrl: string,
	signal?: AbortSignal
): Promise<NewSubsiteViewModel> {
	return environmentHttpClient.get<NewSubsiteViewModel>(
		controllerRelativeUrl + "/GetNewSubsiteViewModel",
		{ siteUrl },
		{ signal }
	);
}

export function getNewSubsiteAvailableTemplates(
	siteUrl: string,
	language: number,
	signal?: AbortSignal
): Promise<SiteTemplateListItem[]> {
	return environmentHttpClient.get<SiteTemplateListItem[]>(
		controllerRelativeUrl + "/GetAvailableTemplates",
		{ siteUrl, language },
		{ signal }
	);
}

export function createSubsite(
	model: CreateSubsiteModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		controllerRelativeUrl + "/CreateNew",
		model,
		{ signal }
	);
}

export type SharePointRoleDefinitionModel = {
	id: number;
	name: string;
	description: string;
};

export type SharePointMemberRoleAssignments = {
	member: SharePointMember;
	roleDefinitionIds: number[];
	canBeChanged: boolean;
};

export type SitePermissionsViewModel = {
	roles: SharePointRoleDefinitionModel[];
	rolesAssignments: SharePointMemberRoleAssignments[];
};

export function getSitePermissionsViewModel(
	siteUrl: string,
	signal?: AbortSignal
): Promise<SitePermissionsViewModel> {
	return environmentHttpClient.get<SitePermissionsViewModel>(
		controllerRelativeUrl + "/GetSitePermissionsViewModel",
		{ siteUrl },
		{ signal }
	);
}

export function findMembers(
	siteUrl: string,
	searchString: string,
	signal?: AbortSignal
): Promise<SharePointPeoplePickerItemModel[]> {
	return environmentHttpClient.get<SharePointPeoplePickerItemModel[]>(
		controllerRelativeUrl + "/FindMembers",
		{
			siteUrl,
			searchString
		},
		{ signal }
	);
}

export type CreateSharePointMemberRoleAssignments = {
	selectedMember: SharePointPeoplePickerItemModel;
	roleDefinitionIds: readonly number[];
};

export type UpdateSharePointMemberRoleAssignments = {
	id: number;
	roleDefinitionIds: readonly number[];
};

export type SharePointMemberChangesForRoleAssignments = {
	create: readonly CreateSharePointMemberRoleAssignments[];
	update: readonly UpdateSharePointMemberRoleAssignments[];
	delete: readonly number[];
};

export type ApplyMembersRoleAssignmentChangesModel = {
	siteUrl: string;
	changes: SharePointMemberChangesForRoleAssignments;
};

export function applyMembersRoleAssignmentChanges(
	siteUrl: string,
	changes: SharePointMemberChangesForRoleAssignments,
	signal?: AbortSignal
): Promise<void> {
	const model: ApplyMembersRoleAssignmentChangesModel = { siteUrl, changes };

	return environmentHttpClient.post<void>(
		controllerRelativeUrl + "/ApplyMembersRoleAssignmentChanges",
		model,
		{ signal }
	);
}
